<script setup>
import { computed, nextTick, onUnmounted, ref, watch } from 'vue';
import { OnClickOutside } from '@vueuse/components';
import { useDebounceFn } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import SearchAPI from 'dashboard/api/search';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';

const props = defineProps({
  conversationId: {
    type: Number,
    default: 0,
  },
  inboxId: {
    type: Number,
    default: 0,
  },
  contactName: {
    type: String,
    default: '',
  },
});

const { t } = useI18n();

const isOpen = ref(false);
const searchQuery = ref('');
const isSearching = ref(false);
const matchedMessages = ref([]);
const activeMatchIndex = ref(-1);
const searchInput = ref(null);

const totalMatches = computed(() => matchedMessages.value.length);
const activeMatchLabel = computed(() => {
  if (!totalMatches.value || activeMatchIndex.value < 0) {
    return `0/${totalMatches.value}`;
  }

  return `${activeMatchIndex.value + 1}/${totalMatches.value}`;
});

const searchPlaceholder = computed(() => {
  if (props.contactName) {
    return t('CONVERSATION.HEADER.SEARCH_MESSAGES_WITH', {
      name: props.contactName,
    });
  }

  return t('CONVERSATION.SEARCH.PLACEHOLDER');
});

const scrollToMatch = index => {
  const message = matchedMessages.value[index];
  if (!message?.id) return;

  activeMatchIndex.value = index;
  emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE, { messageId: message.id });
};

const clearHighlightedMessage = () => {
  emitter.emit(BUS_EVENTS.CLEAR_CONVERSATION_SEARCH_HIGHLIGHT);
};

const resetSearch = () => {
  searchQuery.value = '';
  matchedMessages.value = [];
  activeMatchIndex.value = -1;
  isSearching.value = false;
  clearHighlightedMessage();
};

const closeSearch = () => {
  isOpen.value = false;
  resetSearch();
};

const openSearch = async () => {
  isOpen.value = true;
  await nextTick();
  searchInput.value?.focus();
};

const toggleSearch = () => {
  if (isOpen.value) {
    closeSearch();
    return;
  }

  openSearch();
};

const runSearch = async query => {
  const normalizedQuery = query.trim();

  if (!normalizedQuery || !props.conversationId) {
    matchedMessages.value = [];
    activeMatchIndex.value = -1;
    isSearching.value = false;
    clearHighlightedMessage();
    return;
  }

  isSearching.value = true;

  try {
    const {
      data: {
        payload: { messages = [] },
      },
    } = await SearchAPI.messages({
      q: normalizedQuery,
      inboxId: props.inboxId || undefined,
      conversationId: props.conversationId,
    });

    matchedMessages.value = messages;

    if (messages.length) {
      scrollToMatch(0);
    } else {
      activeMatchIndex.value = -1;
      clearHighlightedMessage();
    }
  } catch {
    matchedMessages.value = [];
    activeMatchIndex.value = -1;
    clearHighlightedMessage();
  } finally {
    isSearching.value = false;
  }
};

const debouncedRunSearch = useDebounceFn(runSearch, 250);

const selectNextMatch = () => {
  if (!totalMatches.value) return;
  const nextIndex = (activeMatchIndex.value + 1) % totalMatches.value;
  scrollToMatch(nextIndex);
};

const selectPreviousMatch = () => {
  if (!totalMatches.value) return;
  const previousIndex =
    (activeMatchIndex.value - 1 + totalMatches.value) % totalMatches.value;
  scrollToMatch(previousIndex);
};

const onSearchInputKeydown = event => {
  if (event.key === 'Enter') {
    event.preventDefault();
    if (event.shiftKey) {
      selectPreviousMatch();
      return;
    }

    selectNextMatch();
  }

  if (event.key === 'Escape') {
    event.preventDefault();
    closeSearch();
  }
};

watch(searchQuery, query => {
  if (!query.trim()) {
    matchedMessages.value = [];
    activeMatchIndex.value = -1;
    isSearching.value = false;
    clearHighlightedMessage();
  }

  debouncedRunSearch(query);
});

watch(
  () => props.conversationId,
  () => {
    if (isOpen.value) {
      resetSearch();
    }
  }
);

onUnmounted(() => {
  resetSearch();
});
</script>

<template>
  <OnClickOutside @trigger="closeSearch">
    <div class="relative">
      <button
        v-tooltip="$t('CONVERSATION.SEARCH_MESSAGES')"
        type="button"
        class="flex h-7 w-7 items-center justify-center rounded-md text-n-slate-11 leading-none hover:bg-n-alpha-2"
        @click.stop="toggleSearch"
      >
        <span class="i-lucide-search inline-flex size-5 shrink-0" />
      </button>

      <transition
        enter-active-class="transition duration-150 ease-out"
        enter-from-class="translate-y-1 scale-95 opacity-0"
        enter-to-class="translate-y-0 scale-100 opacity-100"
        leave-active-class="transition duration-100 ease-in"
        leave-from-class="translate-y-0 scale-100 opacity-100"
        leave-to-class="translate-y-1 scale-95 opacity-0"
      >
        <div
          v-if="isOpen"
          class="absolute right-0 top-full z-50 mt-2 flex min-w-[380px] items-center gap-1 rounded-xl border border-n-brand/40 bg-n-alpha-3 px-2 py-1.5 shadow-lg outline outline-1 outline-n-container backdrop-blur-[100px]"
        >
          <div class="relative min-w-0 flex-1">
            <span
              class="absolute left-3 top-1/2 i-lucide-search size-5 -translate-y-1/2 text-n-slate-11"
            />
            <input
              ref="searchInput"
              v-model="searchQuery"
              type="search"
              :placeholder="searchPlaceholder"
              class="reset-base h-9 w-full rounded-xl border border-n-brand/60 bg-n-surface-1 py-2 pl-11 pr-3 text-sm text-n-slate-12 outline-none"
              @keydown="onSearchInputKeydown"
            />
          </div>

          <span class="min-w-[42px] text-center text-sm text-n-slate-11">
            {{ activeMatchLabel }}
          </span>

          <button
            type="button"
            class="flex h-7 w-7 items-center justify-center rounded-md text-n-slate-11 hover:bg-n-alpha-2 disabled:cursor-not-allowed disabled:opacity-50"
            :disabled="!totalMatches || isSearching"
            @click="selectPreviousMatch"
          >
            <span class="i-lucide-chevron-up inline-flex size-4 shrink-0" />
          </button>

          <button
            type="button"
            class="flex h-7 w-7 items-center justify-center rounded-md text-n-slate-11 hover:bg-n-alpha-2 disabled:cursor-not-allowed disabled:opacity-50"
            :disabled="!totalMatches || isSearching"
            @click="selectNextMatch"
          >
            <span class="i-lucide-chevron-down inline-flex size-4 shrink-0" />
          </button>

          <button
            type="button"
            class="flex h-7 w-7 items-center justify-center rounded-md text-n-slate-11 hover:bg-n-alpha-2"
            @click="closeSearch"
          >
            <span class="i-lucide-x inline-flex size-4 shrink-0" />
          </button>
        </div>
      </transition>
    </div>
  </OnClickOutside>
</template>
